import { Link } from 'react-router-dom';
import type { StatCardModel } from '@/types/stats';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { DataTable } from '@/components/composites/DataTable';
import type { EmployeeWithStats } from '@/data/employees';
import { useEmployeesWithStats } from '@/hooks/useEmployees';
import styles from './TeamStatsPage.module.css';

/** Derive the avg general/day from total - billable. */
function avgGeneralDay(e: EmployeeWithStats): number {
  return +(e.avgHoursDay - e.avgBillableDay).toFixed(1);
}

/** Color class for billable/day: green >= 6.0, amber <= 3.0, else none. */
function billableColor(e: EmployeeWithStats): string {
  if (e.avgBillableDay >= 6.0) return styles.billableGreen;
  if (e.avgBillableDay <= 3.0) return styles.billableAmber;
  return '';
}

export function TeamStatsPage() {
  const { data: members } = useEmployeesWithStats();
  const memberCount = members.length;
  const totalEffectiveDays = members.reduce((s, e) => s + e.effectiveDays, 0);
  const avgHoursDay =
    memberCount > 0 ? members.reduce((s, e) => s + e.avgHoursDay, 0) / memberCount : 0;
  const totalBillable = members.reduce((s, e) => s + e.billableHours, 0);
  const totalHours = members.reduce((s, e) => s + e.totalHours, 0);
  const billableRatio = totalHours > 0 ? Math.round((totalBillable / totalHours) * 100) : 0;

  const cards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Team Members', value: memberCount },
    { label: 'Total Effective Days', value: totalEffectiveDays.toFixed(1) },
    { label: 'Avg Hours/Day', value: `${avgHoursDay.toFixed(1)}h` },
    { label: 'Billable Ratio', value: `${billableRatio}%` },
  ];

  // Compute footer averages
  const avg = (fn: (e: EmployeeWithStats) => number) =>
    memberCount > 0 ? (members.reduce((s, e) => s + fn(e), 0) / memberCount).toFixed(1) : '0';

  return (
    <>
      <PageHeader title="Team Stats" />
      <StatCardGrid cards={cards} />

      <DataTable<EmployeeWithStats>
        caption="Team Performance"
        columns={[
          {
            key: 'rank',
            header: '#',
            width: '40px',
            render: (row) => <span className={styles.rank}>{members.indexOf(row) + 1}</span>,
          },
          {
            key: 'name',
            header: 'Name',
            render: (row) => (
              <Link to={`/employees/${row.id}`} className={styles.nameLink}>
                {row.name}
              </Link>
            ),
          },
          {
            key: 'fullDays',
            header: 'Full Days',
            align: 'right',
            render: (row) => <span className={styles.mono}>{row.fullDays}</span>,
          },
          {
            key: 'halfDays',
            header: 'Half Days',
            align: 'right',
            render: (row) => <span className={styles.mono}>{row.halfDays}</span>,
          },
          {
            key: 'checkIns',
            header: 'Check-ins',
            align: 'right',
            render: (row) => <span className={styles.mono}>{row.checkIns}</span>,
          },
          {
            key: 'effectiveDays',
            header: 'Effective Days',
            align: 'right',
            render: (row) => (
              <span className={`${styles.mono} ${styles.effective}`}>
                {row.effectiveDays.toFixed(1)}
              </span>
            ),
          },
          {
            key: 'totalHours',
            header: 'Total Hours',
            align: 'right',
            render: (row) => <span className={styles.mono}>{row.totalHours.toFixed(1)}h</span>,
          },
          {
            key: 'avgHrsDay',
            header: 'Avg Hrs/Day',
            align: 'right',
            render: (row) => <span className={styles.mono}>{row.avgHoursDay.toFixed(1)}h</span>,
          },
          {
            key: 'avgBillableDay',
            header: 'Avg Billable/Day',
            align: 'right',
            render: (row) => (
              <span className={`${styles.mono} ${billableColor(row)}`}>
                {row.avgBillableDay.toFixed(1)}h
              </span>
            ),
          },
          {
            key: 'avgGeneralDay',
            header: 'Avg General/Day',
            align: 'right',
            render: (row) => <span className={styles.mono}>{avgGeneralDay(row)}h</span>,
          },
        ]}
        rows={[...members]}
        getRowKey={(row) => row.id}
        footer={
          <tr>
            <td />
            <td className={styles.footerLabel}>Average</td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {avg((e) => e.fullDays)}
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {avg((e) => e.halfDays)}
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {avg((e) => e.checkIns)}
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {avg((e) => e.effectiveDays)}
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {avg((e) => e.totalHours)}h
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {avg((e) => e.avgHoursDay)}h
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {avg((e) => e.avgBillableDay)}h
            </td>
            <td className={styles.mono} style={{ textAlign: 'right' }}>
              {avg((e) => avgGeneralDay(e))}h
            </td>
          </tr>
        }
      />
    </>
  );
}
